import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Warehouse as WarehouseIcon, Loader2, Truck, PackageCheck, Tag, CheckCircle2, ZoomIn, X, Upload, Ban, Pencil } from "lucide-react";
import { api, apiError } from "../lib/api";
import { AuthImage } from "../components/AuthImage";
import { useAuth } from "../context/AuthContext";

const STAGE_ORDER = ["PLACED", "PURCHASED", "GUANGZHOU_HUB", "CUSTOMS", "INDO_WAREHOUSE", "DISPATCHED_KEDAI", "READY_PICKUP", "COMPLETED"];

const TXT = {
  id: {
    title_cn: "Portal Gudang Guangzhou", title_jkt: "Portal Gudang Jakarta",
    sub: "Klik tombol untuk menandai barang sudah sampai di gudang. Lacak pakai Kode Marking.",
    markingIn: "Pengajuan Marking Masuk", noMarking: "Belum ada pengajuan marking.",
    pcs: "pcs", hs: "HS Code", markCode: "Kode marking", whAddr: "Alamat gudang",
    sendCode: "Kirim Kode", arrived: "Barang Sampai", inputResi: "Update Status Barang",
    noOrders: "Tidak ada barang yang menunggu di tahap ini.", resi: "Kode Marking",
    markChina: "Tandai Sampai Gudang China", markIndo: "Tandai Sampai Indonesia",
    statusUpdated: "Status diperbarui", fillCodeAddr: "Isi kode marking & alamat gudang",
    codeSent: "Kode marking dikirim", markedArrived: "Barang ditandai sampai gudang",
    statusLbl: "status", kg: "kg",
    st_PENDING: "MENUNGGU", st_APPROVED: "DISETUJUI", st_RECEIVED: "DITERIMA", st_REJECTED: "DITOLAK",
    reject: "Tolak", edit: "Edit", save: "Simpan", cancel: "Batal",
    rejectReason: "Alasan tolak (mis. baterai/cairan tidak bisa kirim udara)",
    uploadProof: "Upload foto bukti sampai", proof: "Lihat bukti", proofUploaded: "Foto bukti terunggah",
    markEdited: "Marking & alamat diperbarui", markRejected: "Barang ditolak", needReason: "Isi alasan penolakan",
  },
  zh: {
    title_cn: "\u5e7f\u5dde\u4ed3\u5e93\u95e8\u6237", title_jkt: "\u96c5\u52a0\u8fbe\u4ed3\u5e93\u95e8\u6237",
    sub: "\u70b9\u51fb\u6309\u94ae\u6807\u8bb0\u8d27\u7269\u5df2\u5230\u4ed3\u3002\u7528\u5507\u5934\u7f16\u7801\u8ffd\u8e2a\u3002",
    markingIn: "\u5f85\u5904\u7406\u5507\u5934\u7533\u8bf7", noMarking: "\u6682\u65e0\u5507\u5934\u7533\u8bf7\u3002",
    pcs: "\u4ef6", hs: "HS\u7f16\u7801", markCode: "\u5507\u5934\u7f16\u7801", whAddr: "\u4ed3\u5e93\u5730\u5740",
    sendCode: "\u53d1\u9001\u7f16\u7801", arrived: "\u8d27\u7269\u5230\u4ed3", inputResi: "\u66f4\u65b0\u8d27\u7269\u72b6\u6001",
    noOrders: "\u5f53\u524d\u9636\u6bb5\u6682\u65e0\u5f85\u5904\u7406\u8d27\u7269\u3002", resi: "\u5507\u5934\u7f16\u7801",
    markChina: "\u6807\u8bb0\u5df2\u5230\u5e7f\u5dde\u4ed3", markIndo: "\u6807\u8bb0\u5df2\u5230\u5370\u5c3c",
    statusUpdated: "\u72b6\u6001\u5df2\u66f4\u65b0", fillCodeAddr: "\u8bf7\u586b\u5199\u5507\u5934\u7f16\u7801\u548c\u4ed3\u5e93\u5730\u5740",
    codeSent: "\u5507\u5934\u7f16\u7801\u5df2\u53d1\u9001", markedArrived: "\u5df2\u6807\u8bb0\u8d27\u7269\u5230\u4ed3",
    statusLbl: "\u72b6\u6001", kg: "\u516c\u65a4",
    st_PENDING: "\u5f85\u5904\u7406", st_APPROVED: "\u5df2\u6279\u51c6", st_RECEIVED: "\u5df2\u5230\u4ed3", st_REJECTED: "\u5df2\u62d2\u7edd",
    reject: "\u62d2\u7edd", edit: "\u7f16\u8f91", save: "\u4fdd\u5b58", cancel: "\u53d6\u6d88",
    rejectReason: "\u62d2\u7edd\u539f\u56e0\uff08\u5982\u7535\u6c60/\u6db2\u4f53\u4e0d\u80fd\u7a7a\u8fd0\uff09",
    uploadProof: "\u4e0a\u4f20\u5230\u4ed3\u51ed\u8bc1", proof: "\u67e5\u770b\u51ed\u8bc1", proofUploaded: "\u51ed\u8bc1\u5df2\u4e0a\u4f20",
    markEdited: "\u5507\u5934\u548c\u5730\u5740\u5df2\u66f4\u65b0", markRejected: "\u8d27\u7269\u5df2\u62d2\u7edd", needReason: "\u8bf7\u586b\u5199\u62d2\u7edd\u539f\u56e0",
  },
};

export default function Warehouse({ lang = "id" }) {
  const t = TXT[lang] || TXT.id;
  const { user } = useAuth();
  const isChina = user?.role === "gudang_china";
  const target = isChina ? "GUANGZHOU_HUB" : "INDO_WAREHOUSE";
  const targetLabel = isChina ? t.markChina : t.markIndo;
  const [orders, setOrders] = useState([]);
  const [markings, setMarkings] = useState([]);
  const [appr, setAppr] = useState({});
  const [busy, setBusy] = useState({});
  const [zoom, setZoom] = useState(null);
  const [edit, setEdit] = useState(null);
  const [proof, setProof] = useState({});
  const [uploading, setUploading] = useState({});

  const load = () => {
    api.get("/orders").then((r) => setOrders(r.data.orders));
    if (isChina) api.get("/markings").then((r) => setMarkings(r.data.markings)).catch(() => {});
  };
  useEffect(load, [isChina]);

  const tidx = STAGE_ORDER.indexOf(target);
  const pending = orders.filter((o) => STAGE_ORDER.indexOf(o.status) < tidx);

  const advance = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try { await api.patch(`/orders/${id}/status`, { status: target }); toast.success(t.statusUpdated); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const approveMark = async (id) => {
    const f = appr[id] || {};
    if (!f.marking_code || !f.warehouse_address) return toast.error(t.fillCodeAddr);
    try { await api.patch(`/markings/${id}/approve`, { marking_code: f.marking_code, warehouse_address: f.warehouse_address, warehouse_note: f.warehouse_note || "" }); toast.success(t.codeSent); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const receiveMark = async (id) => {
    try { await api.patch(`/markings/${id}/received`, { photo_path: proof[id] || "" }); toast.success(t.markedArrived); setProof((p) => ({ ...p, [id]: undefined })); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const uploadProof = async (id, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [id]: true }));
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/uploads", fd); setProof((p) => ({ ...p, [id]: data.path })); toast.success(t.proofUploaded); }
    catch (e) { toast.error(apiError(e)); } finally { setUploading((u) => ({ ...u, [id]: false })); }
  };
  const rejectMark = async (id) => {
    const f = appr[id] || {};
    if (!f.reject_reason) return toast.error(t.needReason);
    try { await api.patch(`/markings/${id}/reject`, { reason: f.reject_reason }); toast.success(t.markRejected); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const startEdit = (m) => { setEdit(m.id); setAppr((s) => ({ ...s, [m.id]: { marking_code: m.marking_code, warehouse_address: m.warehouse_address, warehouse_note: m.warehouse_note || "" } })); };
  const editMark = async (id) => {
    const f = appr[id] || {};
    if (!f.marking_code || !f.warehouse_address) return toast.error(t.fillCodeAddr);
    try { await api.patch(`/markings/${id}/edit`, { marking_code: f.marking_code, warehouse_address: f.warehouse_address, warehouse_note: f.warehouse_note || "" }); toast.success(t.markEdited); setEdit(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const stLabel = (s) => t[`st_${s}`] || s;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-2"><WarehouseIcon className="text-sky-600" size={26} /><h1 className="font-display text-2xl font-bold text-stone-900">{isChina ? t.title_cn : t.title_jkt}</h1></div>
      <p className="text-stone-500 mb-6 text-sm">{t.sub}</p>

      {isChina && (
        <section className="mb-10" data-testid="wh-markings">
          <h2 className="font-display font-semibold text-lg mb-3 flex items-center gap-2"><Tag size={18} />{t.markingIn}</h2>
          <div className="space-y-3">
            {markings.length === 0 && <p className="text-stone-400 text-sm">{t.noMarking}</p>}
            {markings.map((m) => {
              const f = appr[m.id] || {};
              const setF = (k, v) => setAppr((s) => ({ ...s, [m.id]: { ...s[m.id], [k]: v } }));
              return (
                <div key={m.id} data-testid={`wh-mark-${m.id}`} className="bg-white rounded-2xl border border-stone-200 p-4">
                  <div className="flex items-start gap-3">
                    {m.photo_path ? (
                      <button type="button" data-testid={`wh-photo-${m.id}`} onClick={() => setZoom(m)} className="relative w-16 h-16 rounded-lg overflow-hidden shrink-0 group cursor-zoom-in">
                        <AuthImage path={m.photo_path} className="w-16 h-16 object-cover" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition"><ZoomIn size={18} className="text-white opacity-0 group-hover:opacity-100" /></span>
                      </button>
                    ) : <div className="w-16 h-16 rounded-lg bg-stone-100 shrink-0" />}
                    <div className="flex-1">
                      <p className="font-medium text-stone-800 text-sm">{m.product_type} · {m.qty} {t.pcs}</p>
                      {m.hscode && <p className="text-xs text-stone-400">{t.hs}: {m.hscode}</p>}
                      {m.notes && <p className="text-xs text-stone-400">{m.notes}</p>}
                      <span className={`inline-block mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${m.status === "RECEIVED" ? "bg-emerald-100 text-emerald-700" : m.status === "APPROVED" ? "bg-sky-100 text-sky-700" : m.status === "REJECTED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{stLabel(m.status)}</span>
                    </div>
                  </div>
                  {m.status === "PENDING" && (
                    <div className="mt-3 space-y-2">
                      <div className="grid sm:grid-cols-3 gap-2">
                        <input data-testid={`wh-code-${m.id}`} value={f.marking_code || ""} onChange={(e) => setF("marking_code", e.target.value)} placeholder={t.markCode} className="border border-stone-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-sky-400" />
                        <input data-testid={`wh-addr-${m.id}`} value={f.warehouse_address || ""} onChange={(e) => setF("warehouse_address", e.target.value)} placeholder={t.whAddr} className="border border-stone-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-sky-400" />
                        <button data-testid={`wh-approve-${m.id}`} onClick={() => approveMark(m.id)} className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg flex items-center justify-center gap-1.5"><CheckCircle2 size={15} />{t.sendCode}</button>
                      </div>
                      <div className="flex items-center gap-2">
                        <input data-testid={`wh-reject-reason-${m.id}`} value={f.reject_reason || ""} onChange={(e) => setF("reject_reason", e.target.value)} placeholder={t.rejectReason} className="flex-1 border border-stone-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-red-400" />
                        <button data-testid={`wh-reject-${m.id}`} onClick={() => rejectMark(m.id)} className="border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5"><Ban size={15} />{t.reject}</button>
                      </div>
                    </div>
                  )}
                  {m.status === "APPROVED" && edit === m.id && (
                    <div className="mt-3 grid sm:grid-cols-4 gap-2">
                      <input data-testid={`wh-edit-code-${m.id}`} value={f.marking_code || ""} onChange={(e) => setF("marking_code", e.target.value)} placeholder={t.markCode} className="border border-stone-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-sky-400" />
                      <input data-testid={`wh-edit-addr-${m.id}`} value={f.warehouse_address || ""} onChange={(e) => setF("warehouse_address", e.target.value)} placeholder={t.whAddr} className="sm:col-span-2 border border-stone-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-sky-400" />
                      <div className="flex gap-2">
                        <button data-testid={`wh-edit-save-${m.id}`} onClick={() => editMark(m.id)} className="flex-1 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg">{t.save}</button>
                        <button data-testid={`wh-edit-cancel-${m.id}`} onClick={() => setEdit(null)} className="border border-stone-200 text-stone-500 text-sm px-3 py-1.5 rounded-lg">{t.cancel}</button>
                      </div>
                    </div>
                  )}
                  {m.status === "APPROVED" && edit !== m.id && (
                    <div className="mt-3 space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2 bg-sky-50 border border-sky-100 rounded-lg p-2.5 text-xs">
                        <span>{t.markCode} <b className="font-mono">{m.marking_code}</b> · {m.warehouse_address}</span>
                        <button data-testid={`wh-edit-${m.id}`} onClick={() => startEdit(m)} className="inline-flex items-center gap-1 text-sky-700 hover:underline font-semibold"><Pencil size={13} />{t.edit}</button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="inline-flex items-center gap-1.5 border border-dashed border-stone-300 rounded-lg px-3 py-1.5 text-sm text-stone-500 cursor-pointer hover:border-emerald-400">
                          {uploading[m.id] ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />}{proof[m.id] ? t.proofUploaded : t.uploadProof}
                          <input data-testid={`wh-proof-${m.id}`} type="file" accept="image/*" onChange={(e) => uploadProof(m.id, e.target.files[0])} className="hidden" />
                        </label>
                        {proof[m.id] && <AuthImage path={proof[m.id]} className="w-9 h-9 rounded object-cover" />}
                        <button data-testid={`wh-received-${m.id}`} onClick={() => receiveMark(m.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm"><PackageCheck size={14} />{t.arrived}</button>
                      </div>
                    </div>
                  )}
                  {m.status === "RECEIVED" && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 bg-emerald-50 border border-emerald-100 rounded-lg p-2.5 text-xs">
                      <span>{t.markCode} <b className="font-mono">{m.marking_code}</b> · {m.warehouse_address}</span>
                      {m.received_photo_path && <button data-testid={`wh-proof-view-${m.id}`} onClick={() => setZoom({ ...m, photo_path: m.received_photo_path })} className="inline-flex items-center gap-1 text-emerald-700 hover:underline font-semibold"><ZoomIn size={13} />{t.proof}</button>}
                    </div>
                  )}
                  {m.status === "REJECTED" && (
                    <div className="mt-3 bg-red-50 border border-red-100 rounded-lg p-2.5 text-xs text-red-700" data-testid={`wh-rejected-${m.id}`}><b>{t.st_REJECTED}:</b> {m.reject_reason || "-"}</div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <h2 className="font-display font-semibold text-lg mb-3 flex items-center gap-2"><Truck size={18} />{t.inputResi}</h2>
      <div className="space-y-3">
        {pending.length === 0 && <p className="text-stone-400 text-sm" data-testid="wh-no-orders">{t.noOrders}</p>}
        {pending.map((o) => (
          <div key={o.id} data-testid={`wh-order-${o.id}`} className="bg-white rounded-2xl border border-stone-200 p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[180px]">
              <p className="font-mono text-xs text-stone-400">{o.awb}</p>
              <p className="text-sm font-medium text-stone-800">{o.items.map((it) => `${it.title} ×${it.qty}`).join(", ")}</p>
              <p className="text-xs text-stone-400">{o.total_weight_kg} {t.kg} · {t.statusLbl}: {o.status}{o.marking_code ? ` · ${t.resi}: ${o.marking_code}` : ""}</p>
            </div>
            <button data-testid={`wh-advance-${o.id}`} onClick={() => advance(o.id)} disabled={busy[o.id]} className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-60">{busy[o.id] ? <Loader2 className="animate-spin" size={15} /> : <PackageCheck size={15} />}{targetLabel}</button>
          </div>
        ))}
      </div>

      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="wh-zoom-modal" onClick={() => setZoom(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
              <p className="font-semibold text-stone-800 text-sm">{zoom.product_type} · {zoom.qty} {t.pcs}</p>
              <button data-testid="wh-zoom-close" onClick={() => setZoom(null)} className="text-stone-400 hover:text-stone-700"><X size={20} /></button>
            </div>
            <div className="bg-stone-900 flex items-center justify-center max-h-[70vh] overflow-auto">
              <AuthImage path={zoom.photo_path} className="max-h-[70vh] w-auto object-contain" />
            </div>
            <div className="px-4 py-3 text-xs text-stone-500 space-y-1">
              {zoom.hscode && <p>{t.hs}: <span className="font-mono">{zoom.hscode}</span></p>}
              {zoom.notes && <p>{zoom.notes}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
